import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import { API_URL } from '../../config';
import { ConfirmModal } from '../../components/Common/ConfirmModal';
import { useAiWatchlistStrategies } from '../../hooks/useAiWatchlistStrategies';
import './AzureFunctionsScreen.css';

interface ExecutionResult {
    id: string;
    name: string;
    status: 'running' | 'success' | 'error';
    message: string;
    timestamp: Date;
    duration?: number;
}

interface FunctionCardProps {
    icon: string;
    name: string;
    description: string;
    color: 'blue' | 'green' | 'purple' | 'orange';
    isRunning: boolean;
    onExecute: () => void;
}

const FunctionCard: React.FC<FunctionCardProps> = ({ icon, name, description, color, isRunning, onExecute }) => (
    <div className={`function-card ${color}`}>
        <div className="function-card-icon">
            <i className="material-icons">{icon}</i>
        </div>
        <div className="function-card-content">
            <h3>{name}</h3>
            <p>{description}</p>
        </div>
        <button
            className={`function-card-btn ${isRunning ? 'running' : ''}`}
            onClick={onExecute}
            disabled={isRunning}
        >
            {isRunning ? (
                <>
                    <i className="material-icons spin">hourglass_empty</i>
                    <span>En cours...</span>
                </>
            ) : (
                <>
                    <i className="material-icons">play_arrow</i>
                    <span>Exécuter</span>
                </>
            )}
        </button>
    </div>
);

const AzureFunctionsScreen: React.FC = () => {
    const { token } = useAuth();
    const [executions, setExecutions] = useState<ExecutionResult[]>([]);
    const [runningFunctions, setRunningFunctions] = useState<Set<string>>(new Set());

    // Populate form state
    const [populateStart, setPopulateStart] = useState('');
    const [populateEnd, setPopulateEnd] = useState('');
    const [populateForce, setPopulateForce] = useState(false);

    // Individual Report Import form state
    const [reportUrl, setReportUrl] = useState('');
    const [reportSymbol, setReportSymbol] = useState('');

    // Projection Cache form state
    const [projectionYear, setProjectionYear] = useState(new Date().getFullYear().toString());

    // Correct Data form state
    const [correctDate, setCorrectDate] = useState('');
    const [correctLimit, setCorrectLimit] = useState('10');
    const [correctAnalyzeOnly, setCorrectAnalyzeOnly] = useState(false);

    // Toute exécution passe par une confirmation : certaines fonctions ont un effet externe immédiat.
    const [pendingAction, setPendingAction] = useState<{ name: string; message: string; externe: boolean; run: () => void } | null>(null);

    const demanderConfirmation = (endpoint: string, name: string, run: () => void) => {
        const cible = `${endpoint} ${name}`.toLowerCase();
        let effet = "L'action démarre immédiatement sur cet environnement.";
        let externe = false;
        if (/instagram|facebook|social/.test(cible)) {
            effet = 'Cette action publie réellement sur les comptes Instagram et Facebook de Kaneabourse.';
            externe = true;
        } else if (/cache\/invalidate/.test(cible)) {
            effet = 'Cette action vide tout le cache : les prochaines pages seront recalculées et plus lentes.';
            externe = true;
        } else if (/watchlist|narrative|ai[-_]|nia/.test(cible)) {
            effet = "Cette action appelle le modèle d'IA (consommation de tokens) et peut remplacer des contenus visibles par les clients.";
        }
        setPendingAction({ name, message: `Lancer « ${name} » ? ${effet}`, externe, run });
    };

    const executeFunction = (endpoint: string, name: string, body?: Record<string, any>) =>
        demanderConfirmation(endpoint, name, () => runFunction(endpoint, name, body));

    const executeDirectApi = (endpoint: string, name: string) =>
        demanderConfirmation(endpoint, name, () => runDirectApi(endpoint, name));

    const runFunction = async (endpoint: string, name: string, body?: Record<string, any>) => {
        const id = Date.now().toString();
        const startTime = Date.now();

        setRunningFunctions(prev => new Set(prev).add(name));
        setExecutions(prev => [{
            id,
            name,
            status: 'running',
            message: 'Exécution en cours...',
            timestamp: new Date()
        }, ...prev]);

        try {
            if (!token) {
                throw new Error('Session expirée. Veuillez vous reconnecter.');
            }
            const url = `${API_URL}/v1/admin/functions/${endpoint}`;

            const queryParams = body ? '?' + new URLSearchParams(
                Object.entries(body).filter(([_, v]) => v !== undefined && v !== '' && v !== false)
                    .map(([k, v]) => [k, String(v)])
            ).toString() : '';

            const response = await fetch(url + queryParams, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            // Handle auth errors before parsing JSON
            if (response.status === 401 || response.status === 403) {
                const duration = (Date.now() - startTime) / 1000;
                const detail = response.status === 401
                    ? 'Session expirée. Veuillez vous reconnecter.'
                    : 'Permission insuffisante (admin_platform requise).';
                setExecutions(prev => prev.map(e =>
                    e.id === id ? { ...e, status: 'error', message: detail, duration } : e
                ));
                return;
            }

            let data;
            try {
                data = await response.json();
            } catch {
                data = { status: 'error', message: await response.text() };
            }
            const duration = (Date.now() - startTime) / 1000;

            const isSuccess = data?.meta?.success ?? data?.status === 'success';
            const message = data?.meta?.message ?? data?.message ?? JSON.stringify(data);

            setExecutions(prev => prev.map(e =>
                e.id === id ? {
                    ...e,
                    status: isSuccess ? 'success' : 'error',
                    message: message,
                    duration
                } : e
            ));
        } catch (err: any) {
            const duration = (Date.now() - startTime) / 1000;
            setExecutions(prev => prev.map(e =>
                e.id === id ? {
                    ...e,
                    status: 'error',
                    message: `Erreur: ${err.message}`,
                    duration
                } : e
            ));
        } finally {
            setRunningFunctions(prev => {
                const next = new Set(prev);
                next.delete(name);
                return next;
            });
        }
    };

    const handlePopulate = () => {
        executeFunction('populate', 'Populate', {
            start_date: populateStart || undefined,
            end_date: populateEnd || undefined,
            force: populateForce
        });
    };

    const handleCorrectData = () => {
        executeFunction('correct-data', 'Correct Data', {
            date: correctDate || undefined,
            limit: correctLimit,
            analyze_only: correctAnalyzeOnly
        });
    };

    const handleImportIndividualReport = () => {
        if (!reportUrl) {
            alert('Veuillez entrer l\'URL du rapport PDF.');
            return;
        }
        executeFunction('import_individual_report', 'Import Rapport Individuel', {
            url: reportUrl,
            symbol: reportSymbol || undefined
        });
    };

    // AI Generation form state
    const [aiDate, setAiDate] = useState('');
    const [aiForce, setAiForce] = useState(true);
    const [aiSymbol, setAiSymbol] = useState('');
    
    // Nia Indexing form state
    const [niaTableFilter, setNiaTableFilter] = useState('all');
    const [niaSymbol, setNiaSymbol] = useState('');

    const handleAiGenerationAdvanced = () => {
        executeFunction('generate_daily_narratives', 'AI Generation Advanced', {
            date: aiDate || undefined,
            force: aiForce,
            symbol: aiSymbol || undefined
        });
    };

    const handleProjectionCache = () => {
        executeFunction('calculate-projection-cache', 'Projection Cache', {
            target_year: projectionYear ? parseInt(projectionYear) : undefined
        });
    };

    const handleNiaIndexing = () => {
        executeFunction('nia-index', 'Nia RAG Indexing', {
            table_filter: niaTableFilter,
            symbol: niaSymbol || undefined
        });
    };

    // Indice Peur & Avidite : periode de recalcul (vide = tout l'historique)
    const [fearGreedStart, setFearGreedStart] = useState('');
    const [fearGreedEnd, setFearGreedEnd] = useState('');

    const handleFearGreedRebuild = () => {
        const params = new URLSearchParams();
        if (fearGreedStart) params.set('debut', fearGreedStart);
        if (fearGreedEnd) params.set('fin', fearGreedEnd);
        const query = params.toString();
        executeDirectApi(`/v1/admin/fear-greed/rebuild${query ? `?${query}` : ''}`, 'Recalcul Peur & Avidité');
    };

    // AI Watchlist form state
    const [watchlistId, setWatchlistId] = useState('');
    const [watchlistForce, setWatchlistForce] = useState(false);
    const { strategies: watchlists, error: watchlistsError } = useAiWatchlistStrategies();
    // Le nom de l'exécution porte celui de la watchlist : il apparaît dans la confirmation et le journal.
    const selectedWatchlist = watchlists.find(w => String(w.id) === watchlistId);
    const watchlistRunName = selectedWatchlist ? `Refresh AI Watchlist — ${selectedWatchlist.name}` : 'Refresh AI Watchlists';
    const watchlistGenerationRunning = Array.from(runningFunctions).some(n => n.startsWith('Refresh AI Watchlist'));

    const runDirectApi = async (endpoint: string, name: string) => {
        const id = Date.now().toString();
        const startTime = Date.now();

        setRunningFunctions(prev => new Set(prev).add(name));
        setExecutions(prev => [{
            id,
            name,
            status: 'running',
            message: 'Exécution en cours...',
            timestamp: new Date()
        }, ...prev]);

        try {
            if (!token) throw new Error('Session expirée.');

            const response = await fetch(`${API_URL}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 401 || response.status === 403) {
                const duration = (Date.now() - startTime) / 1000;
                setExecutions(prev => prev.map(e =>
                    e.id === id ? { ...e, status: 'error', message: 'Permission insuffisante (admin_platform requise).', duration } : e
                ));
                return;
            }

            let data;
            try { data = await response.json(); } catch { data = { message: await response.text() }; }
            const duration = (Date.now() - startTime) / 1000;

            const isSuccess = response.ok;
            const message = data?.message ?? data?.meta?.message ?? JSON.stringify(data).slice(0, 300);

            setExecutions(prev => prev.map(e =>
                e.id === id ? { ...e, status: isSuccess ? 'success' : 'error', message, duration } : e
            ));
        } catch (err: any) {
            const duration = (Date.now() - startTime) / 1000;
            setExecutions(prev => prev.map(e =>
                e.id === id ? { ...e, status: 'error', message: `Erreur: ${err.message}`, duration } : e
            ));
        } finally {
            setRunningFunctions(prev => { const next = new Set(prev); next.delete(name); return next; });
        }
    };

    const clearHistory = () => setExecutions(prev => prev.filter(e => e.status === 'running'));

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'running': return 'hourglass_empty';
            case 'success': return 'check_circle';
            case 'error': return 'error';
            default: return 'help';
        }
    };

    return (
        <SiteLayout>
            <Header icon='terminal' title='Contrôle Azure Functions' />
            <div className="azure-functions-page">
                {/* Quick Actions Grid */}
                <section className="functions-grid">
                    <FunctionCard
                        icon="calculate"
                        name="Valuation Cache"
                        description="Recalculer les valorisations de toutes les actions (14 modèles : P/E, P/B, DCF, etc.)"
                        color="blue"
                        isRunning={runningFunctions.has('Valuation Cache')}
                        onExecute={() => executeFunction('calculatevaluations', 'Valuation Cache')}
                    />
                    <FunctionCard
                        icon="trending_up"
                        name="Market Cache"
                        description="Recalculer les métriques de marché quotidiennes (volumes, variations, etc.)"
                        color="green"
                        isRunning={runningFunctions.has('Market Cache')}
                        onExecute={() => executeFunction('calculate-market-cache', 'Market Cache')}
                    />
                    <FunctionCard
                        icon="auto_awesome"
                        name="Generate Narratives (AI)"
                        description="Générer les narratifs IA (Valuation & Technique) pour la date du jour."
                        color="purple"
                        isRunning={runningFunctions.has('AI Generation')}
                        onExecute={() => executeFunction('generate_daily_narratives', 'AI Generation')}
                    />
                    <FunctionCard
                        icon="photo_camera"
                        name="Post Instagram/Facebook"
                        description="Générer et publier le résumé quotidien BRVM (image + légende IA) sur Instagram et Facebook."
                        color="purple"
                        isRunning={runningFunctions.has('Post Instagram/Facebook')}
                        onExecute={() => executeFunction('generate_daily_instagram_post', 'Post Instagram/Facebook')}
                    />
                    <FunctionCard
                        icon="calendar_view_week"
                        name="Bilan Hebdo Instagram/Facebook"
                        description="Générer et publier le bilan de la semaine BRVM (indice, top hausses/baisses, volume cumulés) sur Instagram et Facebook."
                        color="purple"
                        isRunning={runningFunctions.has('Bilan Hebdo Instagram/Facebook')}
                        onExecute={() => executeFunction('generate_weekly_instagram_post', 'Bilan Hebdo Instagram/Facebook')}
                    />
                    <FunctionCard
                        icon="emoji_events"
                        name="Calcul Leaderboard"
                        description="Recalculer les performances (TWR/VNI) et mettre à jour le classement des traders."
                        color="orange"
                        isRunning={runningFunctions.has('Leaderboard')}
                        onExecute={() => executeFunction('calculate_leaderboards', 'Leaderboard')}
                    />
                    <FunctionCard
                        icon="bar_chart"
                        name="Projection Cache"
                        description="Recalculer les projections financières (CA, RN, Dividende) pour l'année en cours."
                        color="blue"
                        isRunning={runningFunctions.has('Projection Cache')}
                        onExecute={() => executeFunction('calculate-projection-cache', 'Projection Cache')}
                    />
                    <FunctionCard
                        icon="playlist_add_check"
                        name="Refresh AI Watchlists"
                        description="Régénérer toutes les watchlists ProPicks BRVM via l'IA (Gemini). Simule le trigger mensuel du dernier jour."
                        color="purple"
                        isRunning={runningFunctions.has('Refresh AI Watchlists')}
                        onExecute={() => executeDirectApi('/api/v1/ai-watchlist/admin/generate-all', 'Refresh AI Watchlists')}
                    />
                    <FunctionCard
                        icon="show_chart"
                        name="Maj Perf. Watchlists"
                        description="Calculer les +/-% réels de toutes les watchlists actives (cours actuels vs prix d'entrée)."
                        color="green"
                        isRunning={runningFunctions.has('Maj Perf. Watchlists')}
                        onExecute={() => executeDirectApi('/api/v1/ai-watchlist/admin/update-performance', 'Maj Perf. Watchlists')}
                    />
                    <FunctionCard
                        icon="delete_sweep"
                        name="Invalider le Cache API"
                        description="Forcer le nettoyage de tout le cache (Redis + mémoire locale) des endpoints de la plateforme."
                        color="orange"
                        isRunning={runningFunctions.has('Invalider le Cache API')}
                        onExecute={() => executeDirectApi('/cache/invalidate/all', 'Invalider le Cache API')}
                    />
                </section>

                {/* Advanced Functions */}
                <div className="advanced-section">
                    <h2><i className="material-icons">settings</i> Fonctions Avancées</h2>

                    <div className="advanced-cards">
                        {/* Populate Card */}
                        <div className="advanced-card">
                            <div className="advanced-card-header orange">
                                <i className="material-icons">cloud_download</i>
                                <h3>Import Manuel (Populate)</h3>
                            </div>
                            <div className="advanced-card-body">
                                <p className="card-description">Importer les rapports BRVM pour une plage de dates spécifique.</p>
                                <div className="form-grid">
                                    <div className="form-field">
                                        <label>Date de début</label>
                                        <input
                                            type="date"
                                            value={populateStart}
                                            onChange={e => setPopulateStart(e.target.value)}
                                        />
                                    </div>
                                    <div className="form-field">
                                        <label>Date de fin</label>
                                        <input
                                            type="date"
                                            value={populateEnd}
                                            onChange={e => setPopulateEnd(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <label className="checkbox-field">
                                    <input
                                        type="checkbox"
                                        checked={populateForce}
                                        onChange={e => setPopulateForce(e.target.checked)}
                                    />
                                    <span>Forcer l'exécution (ignorer le verrou)</span>
                                </label>
                            </div>
                            <div className="advanced-card-footer">
                                <button
                                    className={`execute-btn orange ${runningFunctions.has('Populate') ? 'running' : ''}`}
                                    onClick={handlePopulate}
                                    disabled={runningFunctions.has('Populate')}
                                >
                                    {runningFunctions.has('Populate') ? (
                                        <><i className="material-icons spin">sync</i> En cours...</>
                                    ) : (
                                        <><i className="material-icons">play_arrow</i> Lancer l'import</>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Import Individual Report Card */}
                        <div className="advanced-card">
                            <div className="advanced-card-header green">
                                <i className="material-icons">note_add</i>
                                <h3>Import Rapport Individuel</h3>
                            </div>
                            <div className="advanced-card-body">
                                <p className="card-description">Importer et analyser un rapport financier PDF via Gemini IA.</p>
                                <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                                    <div className="form-field">
                                        <label>URL du Rapport (PDF)</label>
                                        <input
                                            type="url"
                                            value={reportUrl}
                                            onChange={e => setReportUrl(e.target.value)}
                                            placeholder="https://www.brvm.org/...pdf"
                                        />
                                    </div>
                                    <div className="form-field">
                                        <label>Symbole (Optionnel)</label>
                                        <input
                                            type="text"
                                            value={reportSymbol}
                                            onChange={e => setReportSymbol(e.target.value)}
                                            placeholder="ex: SNTS, PRSC..."
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="advanced-card-footer">
                                <button
                                    className={`execute-btn green ${runningFunctions.has('Import Rapport Individuel') ? 'running' : ''}`}
                                    onClick={handleImportIndividualReport}
                                    disabled={runningFunctions.has('Import Rapport Individuel') || !reportUrl}
                                >
                                    {runningFunctions.has('Import Rapport Individuel') ? (
                                        <><i className="material-icons spin">sync</i> En cours...</>
                                    ) : (
                                        <><i className="material-icons">play_arrow</i> Importer le rapport</>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Correct Data Card */}
                        <div className="advanced-card">
                            <div className="advanced-card-header purple">
                                <i className="material-icons">build</i>
                                <h3>Correction de Données</h3>
                            </div>
                            <div className="advanced-card-body">
                                <p className="card-description">Analyser et corriger les problèmes de qualité des données.</p>
                                <div className="form-grid">
                                    <div className="form-field">
                                        <label>Date spécifique (optionnel)</label>
                                        <input
                                            type="date"
                                            value={correctDate}
                                            onChange={e => setCorrectDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="form-field">
                                        <label>Nombre max de dates</label>
                                        <input
                                            type="number"
                                            value={correctLimit}
                                            onChange={e => setCorrectLimit(e.target.value)}
                                            min="1"
                                            max="100"
                                        />
                                    </div>
                                </div>
                                <label className="checkbox-field">
                                    <input
                                        type="checkbox"
                                        checked={correctAnalyzeOnly}
                                        onChange={e => setCorrectAnalyzeOnly(e.target.checked)}
                                    />
                                    <span>Analyse seule (pas de correction)</span>
                                </label>
                            </div>
                            <div className="advanced-card-footer">
                                <button
                                    className={`execute-btn purple ${runningFunctions.has('Correct Data') ? 'running' : ''}`}
                                    onClick={handleCorrectData}
                                    disabled={runningFunctions.has('Correct Data')}
                                >
                                    {runningFunctions.has('Correct Data') ? (
                                        <><i className="material-icons spin">sync</i> En cours...</>
                                    ) : (
                                        <><i className="material-icons">play_arrow</i> Lancer l'analyse</>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* AI Generation Card */}
                        <div className="advanced-card">
                            <div className="advanced-card-header blue">
                                <i className="material-icons">auto_awesome</i>
                                <h3>Génération IA Avancée</h3>
                            </div>
                            <div className="advanced-card-body">
                                <p className="card-description">Générer les narratifs (Valuation & Technique) avec options.</p>
                                <div className="form-grid">
                                    <div className="form-field">
                                        <label>Date cible (optionnel)</label>
                                        <input
                                            type="date"
                                            value={aiDate}
                                            onChange={e => setAiDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="form-field">
                                        <label>Symbole spécifique (optionnel)</label>
                                        <input
                                            type="text"
                                            value={aiSymbol}
                                            onChange={e => setAiSymbol(e.target.value)}
                                            placeholder="ex: PRSC, SNTS..."
                                        />
                                    </div>
                                </div>
                                <label className="checkbox-field">
                                    <input
                                        type="checkbox"
                                        checked={aiForce}
                                        onChange={e => setAiForce(e.target.checked)}
                                    />
                                    <span>Forcer la régénération (Ignorer cache)</span>
                                </label>
                            </div>
                            <div className="advanced-card-footer">
                                <button
                                    className={`execute-btn blue ${runningFunctions.has('AI Generation Advanced') ? 'running' : ''}`}
                                    onClick={handleAiGenerationAdvanced}
                                    disabled={runningFunctions.has('AI Generation Advanced')}
                                >
                                    {runningFunctions.has('AI Generation Advanced') ? (
                                        <><i className="material-icons spin">sync</i> En cours...</>
                                    ) : (
                                        <><i className="material-icons">play_arrow</i> Lancer</>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Projection Cache Card */}
                        <div className="advanced-card">
                            <div className="advanced-card-header blue">
                                <i className="material-icons">bar_chart</i>
                                <h3>Projection Cache (Avancé)</h3>
                            </div>
                            <div className="advanced-card-body">
                                <p className="card-description">Recalculer les projections financières pour une année spécifique (CA, RN, Dividende — méthode blend 70/30).</p>
                                <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                                    <div className="form-field">
                                        <label>Année cible</label>
                                        <input
                                            type="number"
                                            value={projectionYear}
                                            onChange={e => setProjectionYear(e.target.value)}
                                            min="2020"
                                            max="2030"
                                            placeholder="ex: 2025"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="advanced-card-footer">
                                <button
                                    className={`execute-btn blue ${runningFunctions.has('Projection Cache') ? 'running' : ''}`}
                                    onClick={handleProjectionCache}
                                    disabled={runningFunctions.has('Projection Cache')}
                                >
                                    {runningFunctions.has('Projection Cache') ? (
                                        <><i className="material-icons spin">sync</i> En cours...</>
                                    ) : (
                                        <><i className="material-icons">play_arrow</i> Lancer le calcul</>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Nia RAG Indexing Card */}
                        <div className="advanced-card">
                            <div className="advanced-card-header purple">
                                <i className="material-icons">psychology</i>
                                <h3>Nia RAG Indexing</h3>
                            </div>
                            <div className="advanced-card-body">
                                <p className="card-description">Rafraîchir manuellement la base de connaissances de Nia.</p>
                                <div className="form-grid">
                                    <div className="form-field">
                                        <label>Données à indexer</label>
                                        <select 
                                            value={niaTableFilter} 
                                            onChange={e => setNiaTableFilter(e.target.value)}
                                            className="admin-select"
                                        >
                                            <option value="all">Toutes les données</option>
                                            <option value="societes">Profils Sociétés</option>
                                            <option value="fundamentals">Fondamentaux</option>
                                            <option value="valuation_cache">Valorisations</option>
                                            <option value="technical_alerts">Alertes Techniques</option>
                                            <option value="ai_summaries">Résumés IA</option>
                                            <option value="daily_market_briefings">Briefings Marché</option>
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label>Symbole boursier (optionnel)</label>
                                        <input
                                            type="text"
                                            value={niaSymbol}
                                            onChange={e => setNiaSymbol(e.target.value.toUpperCase())}
                                            placeholder="ex: ORAC, SNTS..."
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="advanced-card-footer">
                                <button
                                    className={`execute-btn purple ${runningFunctions.has('Nia RAG Indexing') ? 'running' : ''}`}
                                    onClick={handleNiaIndexing}
                                    disabled={runningFunctions.has('Nia RAG Indexing')}
                                >
                                    {runningFunctions.has('Nia RAG Indexing') ? (
                                        <><i className="material-icons spin">sync</i> En cours...</>
                                    ) : (
                                        <><i className="material-icons">auto_awesome</i> Lancer l'indexation</>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* AI Watchlist Card */}
                        <div className="advanced-card">
                            <div className="advanced-card-header purple">
                                <i className="material-icons">playlist_add_check</i>
                                <h3>AI Watchlists ProPicks</h3>
                            </div>
                            <div className="advanced-card-body">
                                <p className="card-description">
                                    Régénérer les sélections IA (Gemini) pour toutes les watchlists ou une seule.
                                    Simule le trigger Azure du dernier jour du mois.
                                </p>
                                <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                                    <div className="form-field">
                                        <label htmlFor="ai-watchlist-select">Watchlist à régénérer</label>
                                        <select
                                            id="ai-watchlist-select"
                                            className="admin-select"
                                            value={watchlistId}
                                            onChange={e => setWatchlistId(e.target.value)}
                                        >
                                            <option value="">Toutes les watchlists</option>
                                            {watchlists.map(w => (
                                                <option key={w.id} value={w.id}>{w.name}</option>
                                            ))}
                                        </select>
                                        {watchlistsError && (
                                            <span style={{ fontSize: '12px', color: '#dc2626', marginTop: 4 }}>
                                                La liste des watchlists n'a pas pu être chargée : seule la régénération de toutes les watchlists est disponible.
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <label className="checkbox-field">
                                    <input
                                        type="checkbox"
                                        checked={watchlistForce}
                                        onChange={e => setWatchlistForce(e.target.checked)}
                                    />
                                    <span>Forcer la révision (ignorer le verrou "déjà révisée ce mois-ci")</span>
                                </label>
                                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: 8 }}>
                                    ⚠️ Cette opération consomme des tokens Gemini et peut prendre 30-60s.
                                    Sans "Forcer", une watchlist déjà révisée ce mois-ci est ignorée silencieusement (statut SKIPPED).
                                </p>
                            </div>
                            <div className="advanced-card-footer" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button
                                    className={`execute-btn orange ${runningFunctions.has('Initialiser structures') ? 'running' : ''}`}
                                    onClick={() => executeDirectApi('/api/v1/ai-watchlist/admin/bootstrap', 'Initialiser structures')}
                                    disabled={runningFunctions.has('Initialiser structures')}
                                >
                                    {runningFunctions.has('Initialiser structures') ? (
                                        <><i className="material-icons spin">sync</i> Initialisation...</>
                                    ) : (
                                        <><i className="material-icons">settings</i> Initialiser structures</>
                                    )}
                                </button>
                                <button
                                    className={`execute-btn purple ${watchlistGenerationRunning ? 'running' : ''}`}
                                    onClick={() => {
                                        const base = watchlistId
                                            ? `/api/v1/ai-watchlist/admin/generate/${watchlistId}`
                                            : '/api/v1/ai-watchlist/admin/generate-all';
                                        const target = watchlistForce ? `${base}?force=true` : base;
                                        executeDirectApi(target, watchlistRunName);
                                    }}
                                    disabled={watchlistGenerationRunning}
                                >
                                    {watchlistGenerationRunning ? (
                                        <><i className="material-icons spin">sync</i> Génération IA...</>
                                    ) : (
                                        <><i className="material-icons">auto_awesome</i> Générer sélections IA{watchlistForce ? ' (forcé)' : ''}</>
                                    )}
                                </button>
                                <button
                                    className={`execute-btn green ${runningFunctions.has('Maj Perf. Watchlists') ? 'running' : ''}`}
                                    onClick={() => executeDirectApi('/api/v1/ai-watchlist/admin/update-performance', 'Maj Perf. Watchlists')}
                                    disabled={runningFunctions.has('Maj Perf. Watchlists')}
                                >
                                    {runningFunctions.has('Maj Perf. Watchlists') ? (
                                        <><i className="material-icons spin">sync</i> Calcul...</>
                                    ) : (
                                        <><i className="material-icons">show_chart</i> Maj performances</>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Indice Peur & Avidite Card */}
                        <div className="advanced-card">
                            <div className="advanced-card-header orange">
                                <i className="material-icons">speed</i>
                                <h3>Indice Peur &amp; Avidité</h3>
                            </div>
                            <div className="advanced-card-body">
                                <p className="card-description">
                                    Recalculer l'historique de l'indice depuis les cotations. Le cache marché quotidien
                                    ne calcule que la dernière séance : ce recalcul remplit ou corrige les séances passées.
                                </p>
                                <div className="form-grid">
                                    <div className="form-field">
                                        <label>Date de début (optionnel)</label>
                                        <input
                                            type="date"
                                            value={fearGreedStart}
                                            onChange={e => setFearGreedStart(e.target.value)}
                                        />
                                    </div>
                                    <div className="form-field">
                                        <label>Date de fin (optionnel)</label>
                                        <input
                                            type="date"
                                            value={fearGreedEnd}
                                            onChange={e => setFearGreedEnd(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: 8 }}>
                                    Dates vides = tout l'historique. N'écrit que dans la table dérivée market_fear_greed ;
                                    relançable sans risque (les séances existantes sont remplacées, jamais dupliquées).
                                </p>
                            </div>
                            <div className="advanced-card-footer">
                                <button
                                    className={`execute-btn orange ${runningFunctions.has('Recalcul Peur & Avidité') ? 'running' : ''}`}
                                    onClick={handleFearGreedRebuild}
                                    disabled={runningFunctions.has('Recalcul Peur & Avidité')}
                                >
                                    {runningFunctions.has('Recalcul Peur & Avidité') ? (
                                        <><i className="material-icons spin">sync</i> Recalcul...</>
                                    ) : (
                                        <><i className="material-icons">refresh</i> Recalculer</>
                                    )}
                                </button>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Execution Log */}
                <section className="log-section">
                    <div className="log-header">
                        <h2><i className="material-icons">history</i> Historique d'exécution</h2>
                        {executions.length > 0 && (
                            <button className="clear-btn" onClick={clearHistory}>
                                <i className="material-icons">delete_sweep</i>
                                Effacer
                            </button>
                        )}
                    </div>
                    {executions.length === 0 ? (
                        <div className="empty-log">
                            <i className="material-icons">inbox</i>
                            <p>Aucune exécution récente</p>
                        </div>
                    ) : (
                        <div className="log-list">
                            {executions.map(exec => (
                                <div key={exec.id} className={`log-item ${exec.status}`}>
                                    <div className="log-item-icon">
                                        <i className={`material-icons ${exec.status === 'running' ? 'spin' : ''}`}>
                                            {getStatusIcon(exec.status)}
                                        </i>
                                    </div>
                                    <div className="log-item-content">
                                        <div className="log-item-header">
                                            <strong>{exec.name}</strong>
                                            <div className="log-item-meta">
                                                {exec.duration && (
                                                    <span className="log-duration">
                                                        <i className="material-icons">timer</i>
                                                        {exec.duration.toFixed(1)}s
                                                    </span>
                                                )}
                                                <span className="log-time">
                                                    {exec.timestamp.toLocaleTimeString('fr-FR')}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="log-item-message">{exec.message}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>
            <ConfirmModal
                isOpen={pendingAction !== null}
                title="Confirmer l'exécution"
                message={pendingAction?.message ?? ''}
                confirmLabel='Exécuter'
                isDestructive={pendingAction?.externe ?? false}
                onConfirm={() => {
                    const action = pendingAction;
                    setPendingAction(null);
                    action?.run();
                }}
                onCancel={() => setPendingAction(null)}
            />
        </SiteLayout>
    );
};

export default AzureFunctionsScreen;
